import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "@shared/ui/Button";
import { Section } from "@shared/ui/Section";
import { TextInput } from "@shared/ui/TextInput";
import { Textarea } from "@shared/ui/Textarea";
import { SegmentedControl } from "@shared/ui/SegmentedControl";
import { addBlockClass, inlineActionClass } from "@shared/ui/inlineAction";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { usePaneManager } from "@features/pane-manager/store";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import {
    refreshDeck,
    releaseShapeSelection,
    selectShapeOnSlide,
    useCurrentSelection,
    useDeck,
    REQUIRED_POWERPOINT_API,
    type ReconciledShape,
    type ReconciledSlide,
} from "@features/office";
import {
    createAssignmentId,
    generationSettingStore,
    assignmentInertReason,
    selectAssignmentsForSlide,
    nextMultiplicity,
    selectUncoveredElements,
    resolveElementName,
    useAssignments,
    useElements,
    useElement,
    useSlide,
    type Assignment,
    type Discretion,
    type ElementSetting,
    type SlideSetting,
} from "@entities/generation-setting";
import { ElementRow } from "@widgets/element-row";

const DISCRETION_ITEMS = [
    { id: "embed", label: "埋め込み" },
    { id: "compose", label: "創造" },
    { id: "propose", label: "提案" },
];

/** まとめて適用の対象。要素と担当で同じ作法を使うため、モードで切り替える。 */
type BulkMode = null | "elements" | "assignments";

/**
 * 画面B。編集中のスライドと、そこに載っている要素のフォーマット設定を扱う。
 *
 * ここが持つのは案件を跨いで動かない恒久設定だけ。実行ごとの旗（スキップ）は画面C。
 * 名前は `PowerPoint.Shape.name` が正本なので、要素の名前はここでは編集しない。
 *
 * 設定の鍵はアドインが発行した識別子で、走査は `deckStore` が 1 箇所で行う。
 * 編集中の枚は選択から引く。
 *
 * 設計: [[2026-08-26 設計ノート アドイン 生成設定UIの再構成]]。
 */
export function SlideResourcePan() {
    const selection = useCurrentSelection();
    const deck = useDeck();

    // 編集中の枚をデッキから引く。選択が取れない間は先頭を出す（枚を開いた直後など、
    // 選択がまだ図形に乗っていないことがある）。
    const slide = useMemo(() => {
        const byId = deck.slides.filter((s) => s.officeSlideId === selection.slideId)[0];
        return byId ?? (selection.slideId === null ? deck.slides[0] : undefined) ?? null;
    }, [deck, selection]);

    if (!slide) {
        // **走査の失敗と「まだ選んでいない」を分ける。** 「PowerPoint でスライドを開いてから」は
        // 後者のための案内で、読み取りに失敗したときには事実と違う（利用者はスライドを開いて
        // いる）。失敗を区別する修正（refs #106）は「他のペインは 0 枚の表示を持たないため
        // 影響しない」としていたが、この画面は 0 枚のときの表示を持っていた（記録 54）。
        // 文言は画面A と揃える。読み直す導線はこの画面の「読み直す」が担う。
        return (
            <>
                <PaneHeader title="スライド" />
                <div className="flex flex-1 items-center justify-center px-4">
                    <p className="text-center text-sm text-text-muted">
                        {deck.failed ? (
                            <>
                                紙面を読み取れませんでした。
                                <br />
                                PowerPoint でスライドを選ぶと読み直します。
                                {/* 原因が Office の版だと画面から分からないので言い添える（記録 34）。 */}
                                {deck.officeTooOld && (
                                    <>
                                        <br />
                                        お使いの PowerPoint が古い可能性があります（PowerPointApi{" "}
                                        {REQUIRED_POWERPOINT_API} 以上が必要です）。
                                    </>
                                )}
                            </>
                        ) : (
                            "PowerPoint でスライドを開いてから操作してください"
                        )}
                    </p>
                </div>
            </>
        );
    }

    // **枚ごとに実体を作り直す。** この画面は編集中の枚を props ではなく選択から引くので、
    // スライドが変わってもコンポーネントは作り直されず、`useState` がすべて残っていた。
    // 「まとめて適用」で 5 枚目の要素を選んだまま 6 枚目へ移ると、一覧は 6 枚目に変わるのに
    // 選択は 5 枚目のままで、押すと**見ていない 5 枚目へ書き込まれる**（記録 56）。上限欄の
    // 打ちかけも前の枚の値を映していた。鍵で分けると、状態が増えても取りこぼしが無い。
    return <SlideResourceBody key={slide.uid} slide={slide} />;
}

function SlideResourceBody({ slide }: { slide: ReconciledSlide }) {
    const [expandedElement, setExpandedElement] = useState<string | null>(null);
    const [countOpen, setCountOpen] = useState(false);
    const [maxDraft, setMaxDraft] = useState<string | null>(null);
    const [bulk, setBulk] = useState<BulkMode>(null);
    const [picked, setPicked] = useState<Set<string>>(new Set());
    // **一括で当てる値は既定を置かない。** 「埋め込み」と「スキップ」を初期値に入れていたため、
    // 開いて押すだけで枚の裁量が「埋め込み」に、担当が全部スキップに書き換わっていた（記録 36）。
    // 取り消しは無く、押した時点で pptx へ保存される。null は「まだ選んでいない」で、
    // このあいだ「適用」は押せない。
    const [bulkDiscretion, setBulkDiscretion] = useState<Discretion | null>(null);
    const [bulkSkip, setBulkSkip] = useState<boolean | null>(null);

    const openPane = usePaneManager((s) => s.openPane);
    // 戻ってきたら出ていった行へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    const selection = useCurrentSelection();
    const assignments = useAssignments();
    const elements = useElements();

    const setting = useSlide(slide.uid);

    const selectedShapeIds = useMemo(() => new Set(selection.shapes.map((s) => s.id)), [selection]);
    const elementUids = useMemo(() => slide.shapes.map((s) => s.uid), [slide]);
    const assignmentIds = useMemo(
        () => selectAssignmentsForSlide(assignments, slide.uid, elementUids),
        [assignments, slide, elementUids]
    );
    const uncovered = useMemo(
        () => selectUncoveredElements(assignments, slide.uid, elementUids),
        [assignments, slide, elementUids]
    );

    const slideId = slide.uid;

    function writeSlide(next: Partial<SlideSetting>): void {
        const base: SlideSetting = setting ?? { updatedAt: "", updatedBy: "" };
        generationSettingStore.writeSlide(slideId, {
            ...base,
            ...next,
            updatedAt: new Date().toISOString(),
            updatedBy: "",
        });
    }

    function writeMultiplicity(next: { counting?: string; max?: number }): void {
        writeSlide({ multiplicity: nextMultiplicity(setting?.multiplicity, next) });
    }

    /** 上限の折りたたみを開け閉てすると、入力途中の下書きは捨てて保存値へ戻す。 */
    function toggleCount(): void {
        setCountOpen((v) => !v);
        setMaxDraft(null);
    }

    /**
     * 要素の行を開け閉てする。**開いた枠は PowerPoint 上でも選び、閉じたら選択を解く。**
     *
     * 図形名は自動採番が大半なので、名前を直すまでは行と紙面の枠が結びつかず、ルールを書く
     * 相手を取り違える。開閉をそのまま紙面へ映して、いまどの枠の話をしているかを示す。
     *
     * 別の行を開くときに前の枠を解く手当ては要らない。`setSelectedShapes` は既存の選択を
     * 置き換えるため、新しい枠を選んだ時点で前の枠は外れる。
     *
     * 紙面への反映は待たない。選択は見せ方であって設定の保存ではないので、失敗しても行の
     * 開閉だけは通す（解除側は `releaseShapeSelection` が自分で握りつぶす）。
     *
     * 設計: [[2026-09-11 設計ノート アドイン 開いた枠を pptx で選ぶ]]。
     */
    function toggleElement(key: string, shape: ReconciledShape): void {
        const closing = expandedElement === key;
        setExpandedElement(closing ? null : key);
        if (closing) {
            void releaseShapeSelection(slide.officeSlideId, shape.officeShapeId);
        } else {
            void selectShapeOnSlide(slide.officeSlideId, shape.officeShapeId).catch(() => {});
        }
    }

    function togglePicked(key: string): void {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    /**
     * まとめて適用を開く。**選択は空で始める。**
     *
     * 開いた時点で全件を選んでいたため、値を選ぶ前に押すと枚の設定がまとめて既定値へ揃って
     * いた（記録 36）。全部に当てたいときはフッターの「全選択」が 1 押しで足りる。
     */
    function startBulk(mode: BulkMode): void {
        setBulk(mode);
        setPicked(new Set());
        setBulkDiscretion(null);
        setBulkSkip(null);
    }

    function cancelBulk(): void {
        setBulk(null);
        setPicked(new Set());
        setBulkDiscretion(null);
        setBulkSkip(null);
    }

    /** 選んだ値がまだ無いあいだは適用させない（既定値での取り違えを塞ぐ・記録 36）。 */
    const bulkValueChosen = bulk === "elements" ? bulkDiscretion !== null : bulkSkip !== null;

    function applyBulk(): void {
        if (!bulkValueChosen) return;
        const now = new Date().toISOString();
        if (bulk === "elements") {
            const entries = Array.from(picked).map((uid) => {
                const base: ElementSetting = generationSettingStore.getElement(uid) ?? {
                    updatedAt: "",
                    updatedBy: "",
                };
                return [
                    uid,
                    { ...base, discretion: bulkDiscretion, updatedAt: now, updatedBy: "" },
                ] as const;
            });
            generationSettingStore.writeElements(entries);
        } else if (bulk === "assignments") {
            const entries: Array<readonly [string, Assignment]> = [];
            for (const id of Array.from(picked)) {
                const base = generationSettingStore.getAssignment(id);
                if (!base) continue;
                entries.push([
                    id,
                    { ...base, skip: bulkSkip ? true : undefined, updatedAt: now, updatedBy: "" },
                ]);
            }
            generationSettingStore.writeAssignments(entries);
        }
        cancelBulk();
    }

    /** 担当へ移る。戻ってきたときこの行へ帰れるよう、出所を控えてから開く。 */
    function openAssignment(id: string): void {
        scroll.leaveFrom(id);
        openPane("assignment", id);
    }

    function addAssignment(): void {
        openAssignment(createAssignmentId());
    }

    return (
        <>
            <PaneHeader title={`${slide.index} ${setting?.name ?? ""}`.trim()} />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto p-3">
                <div className="pb-3">
                    <Section
                        title="スライド"
                        action={
                            <button
                                type="button"
                                className={`${inlineActionClass} flex items-center gap-1 text-interactive-active`}
                                title="スライドを読み直す"
                                onClick={() => void refreshDeck()}
                            >
                                <RefreshCw size={12} />
                                読み直す
                            </button>
                        }
                    >
                        <div className="overflow-hidden rounded-md border border-border-subtle bg-surface">
                            <div className="px-3 py-2">
                                <span className="block text-[11px] text-text-muted">名前</span>
                                <TextInput
                                    value={setting?.name ?? ""}
                                    onChange={(v) => writeSlide({ name: v })}
                                    fullWidth
                                    size="sm"
                                    placeholder="この枚の名前"
                                />
                            </div>
                            <div className="border-t border-text-primary/8 px-3 py-2">
                                <span className="block text-[11px] text-text-muted">ねらい</span>
                                <Textarea
                                    value={setting?.aim ?? ""}
                                    onChange={(v) => writeSlide({ aim: v })}
                                    rows={2}
                                    placeholder="何を伝える場か"
                                />
                            </div>
                            <button
                                type="button"
                                className="flex w-full cursor-pointer items-center gap-2 border-t border-text-primary/8 px-3 py-2 text-left"
                                onClick={toggleCount}
                            >
                                <span className="min-w-0 flex-1 text-[13px]">何枚出すか</span>
                                <span className="shrink-0 text-xs text-text-muted">
                                    {setting?.multiplicity
                                        ? `上限 ${setting.multiplicity.max} 枚`
                                        : "常に 1 枚"}
                                </span>
                                <span className="flex shrink-0 text-text-muted">
                                    {countOpen ? (
                                        <ChevronDown size={16} />
                                    ) : (
                                        <ChevronRight size={16} />
                                    )}
                                </span>
                            </button>
                            {countOpen && (
                                <div className="flex flex-col gap-2 px-3 pb-3">
                                    <label className="block">
                                        <span className="block text-[11px] text-text-muted">
                                            数え方
                                        </span>
                                        <Textarea
                                            value={setting?.multiplicity?.counting ?? ""}
                                            onChange={(v) => writeMultiplicity({ counting: v })}
                                            rows={2}
                                            placeholder="資料から何を数えて何枚にするか"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="block text-[11px] text-text-muted">
                                            削除条件
                                        </span>
                                        <Textarea
                                            value={setting?.deleteWhen ?? ""}
                                            onChange={(v) =>
                                                writeSlide({
                                                    deleteWhen:
                                                        v.trim().length === 0 ? undefined : v,
                                                })
                                            }
                                            rows={2}
                                            placeholder="どういうときにこの枚を消すか（任意）"
                                        />
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span className="min-w-0 flex-1 text-xs text-text-secondary">
                                            上限
                                        </span>
                                        <TextInput
                                            // 入力途中は下書きを映す。ストアへ書ける値だけを保存へ回すと、
                                            // 桁を消している最中に値が戻って 1 文字も消せなくなる。
                                            value={
                                                maxDraft ?? String(setting?.multiplicity?.max ?? 1)
                                            }
                                            onChange={(v) => {
                                                setMaxDraft(v);
                                                const n = Number.parseInt(v, 10);
                                                if (Number.isFinite(n) && n > 0)
                                                    writeMultiplicity({ max: n });
                                            }}
                                            size="sm"
                                            className="w-14"
                                            ariaLabel="上限枚数"
                                        />
                                        <span className="shrink-0 text-[11px] text-text-muted">
                                            枚
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Section>
                </div>

                <div className="pb-3">
                    <Section
                        title="要素"
                        action={
                            bulk === null && elementUids.length > 0 ? (
                                <button
                                    type="button"
                                    className={`${inlineActionClass} text-interactive-active`}
                                    onClick={() => startBulk("elements")}
                                >
                                    まとめて適用
                                </button>
                            ) : undefined
                        }
                    >
                        <div className="overflow-hidden rounded-md border border-border-subtle bg-surface">
                            {slide.shapes.map((shape) => {
                                const key = shape.uid;
                                if (bulk === "elements") {
                                    return (
                                        <label
                                            key={shape.uid}
                                            className={`flex items-center gap-2 border-t border-text-primary/8 px-3 py-2 first:border-t-0 ${key ? "cursor-pointer" : "opacity-40"}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="size-3.5 shrink-0 cursor-pointer accent-brand"
                                                disabled={!key}
                                                checked={key ? picked.has(key) : false}
                                                onChange={() => key && togglePicked(key)}
                                                aria-label="選択"
                                            />
                                            <span className="min-w-0 flex-1 truncate text-[13px]">
                                                {resolveElementName(
                                                    elements,
                                                    shape.uid,
                                                    shape.name
                                                )}
                                            </span>
                                        </label>
                                    );
                                }
                                if (!key) {
                                    return (
                                        <div
                                            key={shape.uid}
                                            className="flex items-center gap-2 border-t border-text-primary/8 px-3 py-2 text-xs text-text-muted first:border-t-0"
                                        >
                                            <span className="min-w-0 flex-1 truncate">
                                                {resolveElementName(
                                                    elements,
                                                    shape.uid,
                                                    shape.name
                                                )}
                                            </span>
                                            <span className="shrink-0">識別子なし</span>
                                        </div>
                                    );
                                }
                                return (
                                    <ElementRowBound
                                        key={key}
                                        elementUid={key}
                                        shape={shape}
                                        expanded={expandedElement === key}
                                        selected={selectedShapeIds.has(shape.officeShapeId)}
                                        onToggle={() => toggleElement(key, shape)}
                                    />
                                );
                            })}
                        </div>
                        {uncovered.length > 0 && bulk === null && (
                            <div className="mt-1.5 flex items-start gap-2 rounded bg-warning/10 px-2 py-1.5 text-xs text-warning">
                                <TriangleAlert size={12} className="mt-0.5 shrink-0" />
                                <span>どの担当にも名指されていない枠が {uncovered.length}</span>
                            </div>
                        )}
                    </Section>
                </div>

                <div className="pb-3">
                    <Section
                        title="この枚を名指している生成担当"
                        action={
                            bulk === null && assignmentIds.length > 0 ? (
                                <button
                                    type="button"
                                    className={`${inlineActionClass} text-interactive-active`}
                                    onClick={() => startBulk("assignments")}
                                >
                                    まとめて適用
                                </button>
                            ) : undefined
                        }
                    >
                        {assignmentIds.length === 0 ? (
                            <p className="text-xs text-warning">生成担当なし</p>
                        ) : (
                            <div className="overflow-hidden rounded-md border border-border-subtle bg-surface">
                                {assignmentIds.map((id) => {
                                    const a = assignments[id];
                                    const reason = assignmentInertReason(a);
                                    if (bulk === "assignments") {
                                        return (
                                            <label
                                                key={id}
                                                className="flex cursor-pointer items-center gap-2 border-t border-text-primary/8 px-3 py-2 first:border-t-0"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="size-3.5 shrink-0 cursor-pointer accent-brand"
                                                    checked={picked.has(id)}
                                                    onChange={() => togglePicked(id)}
                                                    aria-label="選択"
                                                />
                                                <span className="min-w-0 flex-1 truncate text-[13px]">
                                                    {a.label}
                                                </span>
                                            </label>
                                        );
                                    }
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            data-anchor={id}
                                            className="flex w-full cursor-pointer items-center gap-2 border-t border-text-primary/8 px-3 py-2 text-left first:border-t-0"
                                            onClick={() => openAssignment(id)}
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[13px]">
                                                    {a.label}
                                                </span>
                                                <span className="block text-[11px] text-text-muted">
                                                    {a.instruction.length.toLocaleString()} 字
                                                    {reason !== null && (
                                                        <span className="text-warning">
                                                            {" "}
                                                            ・{" "}
                                                            {reason === "skip"
                                                                ? "スキップ中"
                                                                : reason === "allTargetsSkipped"
                                                                  ? "全対象が書き込み対象外のため実行されません"
                                                                  : "対象がないため実行されません"}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                            <ChevronRight
                                                size={16}
                                                className="shrink-0 text-text-muted"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {bulk === null && (
                            <button
                                type="button"
                                className={`${addBlockClass} mt-1.5`}
                                onClick={addAssignment}
                            >
                                <Plus size={12} />
                                この枚を名指す担当を足す
                            </button>
                        )}
                    </Section>
                </div>
            </div>

            {bulk !== null && (
                <PaneFooter>
                    <div className="flex w-full flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 text-xs text-text-secondary">
                                {picked.size} 件を選択中
                            </span>
                            <button
                                type="button"
                                className="cursor-pointer text-xs text-interactive-active"
                                onClick={() =>
                                    setPicked(
                                        new Set(bulk === "elements" ? elementUids : assignmentIds)
                                    )
                                }
                            >
                                全選択
                            </button>
                        </div>
                        {bulk === "elements" ? (
                            <div className="flex items-center gap-2">
                                <span className="w-10 shrink-0 text-xs text-text-secondary">
                                    裁量
                                </span>
                                <SegmentedControl
                                    items={DISCRETION_ITEMS}
                                    value={bulkDiscretion ?? ""}
                                    onChange={(v) => setBulkDiscretion(v as Discretion)}
                                />
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="w-10 shrink-0 text-xs text-text-secondary">
                                    実行
                                </span>
                                <SegmentedControl
                                    items={[
                                        { id: "run", label: "する" },
                                        { id: "skip", label: "スキップ" },
                                    ]}
                                    value={bulkSkip === null ? "" : bulkSkip ? "skip" : "run"}
                                    onChange={(v) => setBulkSkip(v === "skip")}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 text-[11px] text-text-muted">
                                {picked.size === 0
                                    ? "適用する項目を選んでください"
                                    : !bulkValueChosen
                                      ? "適用する値を選んでください"
                                      : `${picked.size} 件に適用します`}
                            </span>
                            <Button
                                size="sm"
                                onClick={applyBulk}
                                disabled={picked.size === 0 || !bulkValueChosen}
                            >
                                適用
                            </Button>
                            <Button variant="secondary" size="sm" onClick={cancelBulk}>
                                キャンセル
                            </Button>
                        </div>
                    </div>
                </PaneFooter>
            )}
        </>
    );
}

/** 要素 1 行をストアへ束ねる。行ごとに購読を分け、1 件の編集で他行を再レンダーさせない。 */
function ElementRowBound({
    elementUid,
    shape,
    expanded,
    selected,
    onToggle,
}: {
    elementUid: string;
    shape: ReconciledShape;
    expanded: boolean;
    selected: boolean;
    onToggle: () => void;
}) {
    const setting = useElement(elementUid);
    // 名前の正本は settings。無ければ図形名へ落ちる。
    const name = setting?.name?.trim();
    return (
        <ElementRow
            shape={{
                name: name && name.length > 0 ? name : shape.name,
                shapeName: shape.name,
                kind: shape.kind,
            }}
            setting={setting}
            expanded={expanded}
            selected={selected}
            onToggle={onToggle}
            onChange={(next) => generationSettingStore.writeElement(elementUid, next)}
        />
    );
}
